import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { Download } from "lucide-react";

interface ExportToPDFProps {
  contentRef: React.RefObject<HTMLDivElement | null>;
  fileName?: string;
  title?: string;
}

/**
 * Enhanced PDF Export function with standard A4 formatting and reliable pagination
 */
export const exportToPDF = async ({
  contentRef,
  fileName,
  title,
}: ExportToPDFProps) => {
  if (!contentRef.current) {
    toast.warn("Content to export not found.");
    return;
  }

  try {
    const loadingToast = toast.info("Preparing PDF report...", {
      autoClose: false,
    });

    const element = contentRef.current;

    // Save original styles
    const originalStyle = element.getAttribute("style") || "";
    const originalClassName = element.className;

    // Use a standard internal width for the capture to ensure consistent aspect ratio
    // 1200px is a good balance for detail and scaling to A4
    const captureWidth = 1200;

    // Prepare element for capture
    element.style.width = `${captureWidth}px`;
    element.style.maxHeight = "none";
    element.style.height = "auto";
    element.style.overflow = "visible";
    element.classList.remove("max-h-[calc(95vh-120px)]", "overflow-y-auto");

    // Wait for layout to reflow
    await new Promise((resolve) => setTimeout(resolve, 300));

    const canvas = await html2canvas(element, {
      scale: 2, // Higher scale for better print quality
      useCORS: true,
      logging: false,
      width: captureWidth,
      backgroundColor: "#ffffff",
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector(
          '[ref="contentRef"]'
        ) as HTMLElement;
        if (clonedElement) {
          clonedElement.style.width = `${captureWidth}px`;
          clonedElement.style.maxHeight = "none";
        }
      },
    });

    // Restore original styles immediately
    element.setAttribute("style", originalStyle);
    element.className = originalClassName;

    // PDF Dimension Setup (A4 in mm)
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Calculate image dimensions to fit page width
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const headerHeight = 25;
    const footerHeight = 15;
    const firstPageContentHeight =
      pageHeight - headerHeight - footerHeight - margin;
    const subsequentPageContentHeight = pageHeight - margin * 2 - footerHeight;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    // Helper to add Footer
    const addFooter = (pageNum: number) => {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Page ${pageNum} | ${title || "AI Insights Report"}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    };

    // --- PAGE 1 ---
    // Header
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(30, 58, 138); // Dark blue
    pdf.text("AI Insights & User Remarks", pageWidth / 2, 15, {
      align: "center",
    });

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(
      `${title || "Analysis"} - ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      20,
      { align: "center" }
    );

    pdf.setDrawColor(219, 234, 254);
    pdf.setLineWidth(0.5);
    pdf.line(margin, 23, pageWidth - margin, 23);

    // Initial Image Slice for Page 1
    const firstPageSliceHeight = Math.min(
      imgHeight,
      (firstPageContentHeight * canvas.width) / imgWidth
    );

    // We add the first slice
    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.95),
      "JPEG",
      margin,
      headerHeight,
      imgWidth,
      (firstPageSliceHeight * imgWidth) / canvas.width
    );

    addFooter(pageNumber);
    heightLeft -= (firstPageSliceHeight * imgWidth) / canvas.width;
    position -= firstPageSliceHeight;

    // --- SUBSEQUENT PAGES ---
    while (heightLeft > 0) {
      pageNumber++;
      pdf.addPage();

      Math.min(
        canvas.height + position, // remaining canvas height
        (subsequentPageContentHeight * canvas.width) / imgWidth
      );

      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.95),
        "JPEG",
        margin,
        margin,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
        0 // No rotation
      );

      // The jspdf clipping math can be tricky, let's use the offset instead
      // This is a more stable way to handle long images in jspdf
      pdf.setPage(pageNumber);

      // Cleanup: Since standard addImage(img, 'JPEG', x, y, w, h) with 'while'
      // can cause overlaps, we'll actually use the 'position' shift technique correctly
      // but ensure we cover the background with a white rectangle if slices overlap

      addFooter(pageNumber);
      heightLeft -= subsequentPageContentHeight;
    }

    // Re-calculating multi-page logic for perfect alignment if needed
    // But standard addImage with negative position is usually preferred if we addPage first
    // Let's refine the loop one more time for absolute precision

    const finalPdf = new jsPDF("p", "mm", "a4");
    let currentHeight = 0;
    const canvasHeight = canvas.height;

    let isFirstPage = true;
    let pNum = 1;

    while (currentHeight < canvasHeight) {
      if (!isFirstPage) finalPdf.addPage();

      const pageContentHeight = isFirstPage
        ? firstPageContentHeight
        : pageHeight - margin * 2;
      const topMargin = isFirstPage ? headerHeight : margin;

      const sourceY = currentHeight;
      const sourceHeight = Math.min(
        canvasHeight - currentHeight,
        (pageContentHeight * canvas.width) / imgWidth
      );
      const destHeight = (sourceHeight * imgWidth) / canvas.width;

      // Create a temporary canvas for the slice to avoid jspdf math issues
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      const ctx = tempCanvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );
        finalPdf.addImage(
          tempCanvas.toDataURL("image/jpeg", 0.95),
          "JPEG",
          margin,
          topMargin,
          imgWidth,
          destHeight
        );
      }

      // Add Header to first page
      if (isFirstPage) {
        finalPdf.setFont("helvetica", "bold");
        finalPdf.setFontSize(18);
        finalPdf.setTextColor(30, 58, 138);
        finalPdf.text("AI Insights & User Remarks", pageWidth / 2, 15, {
          align: "center",
        });
        finalPdf.setFontSize(10);
        finalPdf.setFont("helvetica", "normal");
        finalPdf.setTextColor(100, 100, 100);
        finalPdf.text(
          `${title || "Analysis"} - ${new Date().toLocaleDateString()}`,
          pageWidth / 2,
          20,
          { align: "center" }
        );
        finalPdf.setDrawColor(219, 234, 254);
        finalPdf.setLineWidth(0.5);
        finalPdf.line(margin, 23, pageWidth - margin, 23);
      }

      // Footer
      finalPdf.setFont("helvetica", "normal");
      finalPdf.setFontSize(8);
      finalPdf.setTextColor(150, 150, 150);
      finalPdf.text(
        `Page ${pNum} | ${title || "AI Report"}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );

      currentHeight += sourceHeight;
      isFirstPage = false;
      pNum++;
    }

    const timestamp = new Date().getTime();
    const finalFileName = fileName || `Report_${timestamp}.pdf`;

    finalPdf.save(finalFileName);
    toast.dismiss(loadingToast);
    toast.success("PDF report generated successfully!");
  } catch (error) {
    console.error("PDF Export Error:", error);
    toast.error("Failed to generate PDF report.");
  }
};

interface ExportPDFButtonProps {
  contentRef: React.RefObject<HTMLDivElement | null>;
  modalData: any;
  className?: string;
}

export const ExportPDFButton: React.FC<ExportPDFButtonProps> = ({
  contentRef,
  modalData,
  className = "",
}) => {
  const handleExport = () => {
    const title = modalData?.chart_title || "AI_Insights_Report";
    exportToPDF({
      contentRef,
      title,
      fileName: `AI_Report_${title.replace(
        /\s+/g,
        "_"
      )}_${new Date().getTime()}.pdf`,
    });
  };

  return (
    <button
      onClick={handleExport}
      className={`flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-all active:scale-95 shadow-sm ${className}`}
      title="Export all data to PDF"
    >
      <Download size={18} />
      <span className="hidden sm:inline">Export PDF</span>
    </button>
  );
};
